import {
  Breadcrumbs as MUIBreadcrumbs,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { Link } from 'react-router-dom';

import ChevronRight from '../../assets/chevron-right.svg?react';
import Flex from '../Flex/Flex';

import type { CSBreadcrumbsProps } from './CSBreadcrumbs.model';

/**
 * Chevron-right separator component for CSBreadcrumbs
 * Uses a chevron-right icon wrapped in a Flex for consistent sizing
 */
const ChevronRightSeparator = () => (
  <Flex center style={{ height: 12, width: 12 }}>
    <ChevronRight />
  </Flex>
);

/**
 * CSBreadcrumbs component - A design-system-compliant breadcrumb navigation
 *
 * This component enforces consistent breadcrumb rendering across the application
 * by accepting a config array rather than allowing arbitrary children.
 *
 * Design system specifications:
 * - Uses caption_medium typography
 * - Links styled with brandPrimary color
 * - Terminal breadcrumb styled with textPrimary color
 * - Chevron-right separator with 4px inline margin
 *
 * @example
 * ```tsx
 * import { CSBreadcrumbs } from '@brightdrop/bd-ui';
 *
 * const breadcrumbs = [
 *   { label: 'Home', href: '/', dataTestId: 'home-link' },
 *   { label: 'Products', href: '/products', dataTestId: 'products-link' },
 *   { label: 'Current Page', dataTestId: 'current-page' }
 * ];
 *
 * <CSBreadcrumbs items={breadcrumbs} dataTestId="page-breadcrumbs" />
 * ```
 */
const CSBreadcrumbs: React.FC<CSBreadcrumbsProps> = ({
  items,
  isLoading = false,
  dataTestId,
}) => {
  const theme = useTheme();

  // Show skeleton loader if loading
  if (isLoading) {
    // Determine number of skeletons to show
    const skeletonCount = items && items.length > 0 ? items.length : 2;
    // Widths proportional to typical breadcrumb text at 12px font size
    const skeletonWidths = [40, 70, 100, 85, 60];
    const skeletonItems = Array.from({ length: skeletonCount }, (_, index) => ({
      id: `breadcrumb-skeleton-${index}`,
      width: skeletonWidths[index % skeletonWidths.length],
    }));

    return (
      <MUIBreadcrumbs
        {...(dataTestId && { 'data-testid': dataTestId })}
        separator={<ChevronRightSeparator />}
        sx={{
          '& .MuiBreadcrumbs-separator': {
            marginInline: '4px',
          },
        }}
      >
        {skeletonItems.map((skeleton) => (
          <Skeleton key={skeleton.id} width={skeleton.width} height={16} />
        ))}
      </MUIBreadcrumbs>
    );
  }

  // Don't render if items not ready
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <MUIBreadcrumbs
      {...(dataTestId && { 'data-testid': dataTestId })}
      separator={<ChevronRightSeparator />}
      sx={{
        '& .MuiBreadcrumbs-separator': {
          marginInline: '4px',
        },
        '& a': {
          ...theme.typography.caption_medium,
          color: theme.new.color.brandPrimary,
          textDecoration: 'none',
          display: 'flex',
        },
      }}
    >
      {items.map((item, index) => {
        const isTerminal = index === items.length - 1;
        const key = `breadcrumb-${item.dataTestId || item.label || index}`;

        // Terminal breadcrumb (current page) - render as Typography
        if (isTerminal || !item.href) {
          return (
            <Typography
              key={key}
              variant="caption_medium"
              sx={{ color: theme.new.color.textPrimary, display: 'flex' }}
              {...(item.dataTestId && { 'data-testid': item.dataTestId })}
            >
              {item.label}
            </Typography>
          );
        }

        // Navigation breadcrumb - render as Link
        return (
          <Link
            key={key}
            to={item.href}
            {...(item.dataTestId && { 'data-testid': item.dataTestId })}
          >
            {item.label}
          </Link>
        );
      })}
    </MUIBreadcrumbs>
  );
};

export default CSBreadcrumbs;
